import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../store/hooks';
import { logoutAsync } from '../store/slices/authSlice';
import { addToast } from '../store/slices/toastsSlice';
import './Sidebar.css';

interface NavItem {
  path: string;
  label: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigationSections: NavSection[] = [
  {
    title: 'Main',
    items: [
      { path: '/', label: 'Dashboard' },
      { path: '/workflow', label: 'Workflow' },
      { path: '/orders', label: 'Orders' },
      { path: '/clients', label: 'Clients' },
      { path: '/schedule', label: 'Schedule' },
      { path: '/inventory', label: 'Inventory' },
      { path: '/pricing', label: 'Pricing' }
    ]
  },
  {
    title: 'Recipes',
    items: [
      { path: '/fillings', label: 'Fillings' },
      { path: '/components', label: 'Components' }
    ]
  },
  {
    title: 'Tracking',
    items: [
      { path: '/completed-orders', label: 'Completed Orders' },
      { path: '/expenses', label: 'Expenses' },
      { path: '/insights', label: 'Insights' }
    ]
  },
  {
    title: 'Marketing',
    items: [
      { path: '/marketing/mass-email', label: 'Mass Email' },
      { path: '/marketing/contact-parser', label: 'Contact Parser' },
      { path: '/marketing/order-parser', label: 'Order Parser' }
    ]
  },
  {
    title: 'HACCP',
    items: [
      { path: '/suppliers', label: 'Suppliers' },
      { path: '/haccp/ingredient-tracking', label: 'Ingredient Tracking' },
      { path: '/haccp/product-tracking', label: 'Product Tracking' },
      { path: '/haccp/temperature-records', label: 'Temperature Records' },
      { path: '/haccp/sanitation-records', label: 'Sanitation Records' },
      { path: '/haccp/admin-review', label: 'Admin Review' }
    ]
  },
  {
    title: 'Lunch Orders',
    items: [
      { path: '/lci-lunch-orders', label: 'LCI Lunch Orders' }
    ]
  },
  {
    title: 'Admin',
    items: [
      { path: '/admin/manage-team', label: 'Manage Team' },
      { path: '/admin/profile', label: 'Profile' }
    ]
  }
];

export const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    if (isMobile || isTablet) {
      setIsOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutAsync()).unwrap();
      closeSidebar();
      navigate('/login');
      dispatch(addToast('Logged out successfully', 'success'));
    } catch (error) {
      dispatch(addToast('Logout failed', 'error'));
    }
  };

  const sidebarClass = `sidebar ${isOpen ? 'sidebar--open' : ''} ${
    isMobile ? 'sidebar--mobile' : isTablet ? 'sidebar--tablet' : 'sidebar--desktop'
  }`;

  return (
    <>
      {/* Hamburger Button */}
      {(isMobile || isTablet) && (
        <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle navigation">
          <span className="hamburger-icon">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      )}

      {/* Overlay for mobile and tablet */}
      {(isMobile || isTablet) && isOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      {/* Sidebar */}
      <aside className={sidebarClass}>
        <div className="sidebar-header">
          <h2 className="sidebar-logo">Deb's Meatbuns</h2>
        </div>

        <nav className="sidebar-nav">
          {navigationSections.map((section, index) => (
            <div key={index} className="nav-section">
              <h3 className="nav-section-title">{section.title}</h3>
              <ul className="nav-list">
                {section.items.map(item => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => `nav-link ${isActive ? 'nav-link--active' : ''}`}
                      onClick={closeSidebar}
                      end={item.path === '/'}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Logout Section */}
          <div className="nav-section nav-section--logout">
            <button
              className="nav-link nav-link--logout"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
};
